import { View } from "extensions/marionette";
import { captureException } from "extensions/sentry";

import { store } from "common/store";
import searchPageSvg from "img/search-page.svg";

import { SET_ACTIVE_QUALIFIERS } from "common/actions/common";

import { buildSourceData } from "common/components/filterBar/BuildSrcData";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { pazienHistory } from "common/components/pazienHistory";
import { getAllPivotsWithValues } from "common/helpers/dimensionHelpers";

import * as SearchTxnsConfigs from "./forms/config";
import { SearchTypeOption } from "./searchTypeOptions";
import { SearchForm } from "./forms/searchForm";

import { SearchTransactions, SearchTransactionsById } from "./searchTransactions";

import "./search.scss";

export const SearchView = View.extend({
    template: () => `<header>
                        <img class="search-page-icon" src="${searchPageSvg}">
                        <span>Search transactions</span>
                     </header>
                     <div class="form-header">
                        <div class="row">
                            <div class="col-xs-10 col-sm-10 directions">
                                <i class="fas fa-asterisk"></i><span>At least one primary search field is required</span>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-xs-4 col-sm-4 section-label"><i class="fas fa-asterisk form-asterisk"></i> Search for</div>
                            <div class="col-xs-7 col-sm-7 sections"></div>
                        </div>
                     </div>
                     <div class="criteria"></div>
                     <div class="results_container">
                        <header class="results_header">
                            <i class="far fa-list-ul list-icon" aria-hidden="true"></i>
                            <i class="far fa-check results-icon" aria-hidden="true"></i>
                            <span>Search results</span>
                        </header>
                        <div class="results"></div>
                     </div>
                    `,
    className: "search full-page-wrapper",
    regions: {
        sections: ".sections",
        criteria: ".criteria",
        results: ".results"
    },
    ui: {
        results: ".results_container"
    },
    childViewEvents: {
        "form:submitted": "formSubmitted",
        "form:cleared": "formCleared",
        "change:section": "changeSection"
    },

    changeSection(section) {
        pazienHistory.navigateWithParams(`search/${section}/`, { trigger: true });
    },
    
    getSearchOptions() {
        return this.getOption("qualifiers");
    },
    
    getSearchTypeLabels: () => ({
        sales_refunds: store.getState().common.merchant_capabilities.has_oct
            ? "Sales, refunds and payouts"
            : "Sales and refunds",
        chargebacks: "Chargebacks and retrieval requests",
        authorizations: "Authorizations"
    }),
    
    onRender() {
        this.showChildView("sections", new SearchTypeOption({
            section: this.getOption("txn_type"),
            typeLabels: this.getSearchTypeLabels()
        }));
        
        this.renderSectionForm();
    },
    
    async renderSectionForm() {
        try {
            const dimensions = await getAllPivotsWithValues();
            
            if (this.isDestroyed()) return;
    
            const qualifiers = this.getSearchOptions();

            buildSourceData({ sources: "*", addUniqueId: false });
    
            this.showChildView("criteria", new SearchForm({
                section: this.getOption("txn_type"),
                qualifiers,
                dimensions,
                filterBarData: store.getState().common.filterBarData.all,
                formConfig: SearchTxnsConfigs[this.getOption("txn_type")]
            }));
    
            if (qualifiers.toString()) {
                this.getTransactions(qualifiers);
            }
        } catch (error) {
            captureException(error);
        }
    },
    
    formSubmitted(e, data) {
        const searchQualifiers = new Qualifiers();
        Object.entries(data).forEach(([key, value]) => {
            const normalizedValue = Qualifiers.normalizeValue(value.trim());
            
            if (key && normalizedValue && typeof value === "string") {
                searchQualifiers.addOrReplace(`${key}:${normalizedValue}`);
            }
        });
        const fragment = `search/${this.getOption("txn_type")}/${searchQualifiers.toString(true)}`;
        pazienHistory.navigateWithParams(fragment, { trigger: false });
        SET_ACTIVE_QUALIFIERS(searchQualifiers.toString(true));
        this.getTransactions(searchQualifiers);
    },
    
    formCleared(e) {
        const fragment = `search/${this.getOption("txn_type")}/`;
        pazienHistory.navigateWithParams(fragment, { trigger: false, replace: true });
        const region = this.getRegion("results");
        region.empty();
    },
    
    getTransactions(searchQualifiers) {
        let apiSubresource;
        if (this.getOption("txn_type") === "sales_refunds") {
            apiSubresource = "settlements";
            this.$el.addClass("settlements");
        } else if (this.getOption("txn_type") === "chargebacks") {
            apiSubresource = "chargebacks";
            this.$el.addClass("chargebacks");
        } else if (this.getOption("txn_type") === "authorizations") {
            apiSubresource = "authorizations";
            this.$el.addClass("authorizations");
        }

        let tableView;
        if (searchQualifiers.has("pz_transaction_ref")) {
            tableView = (
                <SearchTransactionsById
                    className="transactions"
                    section={apiSubresource}
                    apiSubresource={apiSubresource}
                    pz_transaction_ref={searchQualifiers.get("pz_transaction_ref")[0]}
                    queryParams={this.getOption("queryParams")}
                    suggestedNamePrefix="search"
                    paginatorTop
                />
            );
        } else {
            tableView = (
                <SearchTransactions
                    className="transactions"
                    section={apiSubresource}
                    apiSubresource={apiSubresource}
                    queryParams={this.getOption("queryParams")}
                    suggestedNamePrefix="search"
                    paginatorTop
                />
            );
        }
 
        this.showChildView("results", tableView, { waitForAttach: true });
        
        this.ui.results.addClass("on");
        
        const searchContainer = document.querySelector(".results_container");
        if (searchContainer) {
            searchContainer.scrollIntoView(true);
        }
    }
});
